// CORS proxy helper for browser-based API calls
//
// On HOSTED domains (verigit.com, *.pages.dev, etc):
//   Try direct fetch first — many APIs (Linear, Bitbucket) allow CORS
//   from real origins. If a direct call fails with a network/CORS error,
//   fall back to the same-origin CORS proxy (/__cors-proxy).
//   Jira/Atlassian uses a dedicated /api/jira route (no generic proxy).
//
// On LOCALHOST / dev:
//   Always route through the dev CORS proxy (localhost:3456) since
//   browsers block cross-origin requests from localhost.
//
// No external proxies — your data only touches your own infrastructure + the target API.

const DEV_PROXY = 'http://localhost:3456';
const isDev = typeof window !== 'undefined' && (window.location.port === '5173' || window.location.hostname === 'localhost');

// Track which API origins are known to need the proxy (CORS failures cache)
const corsBlockedOrigins = new Set<string>();

function getOrigin(url: string): string {
  try { return new URL(url).origin; } catch { return url; }
}

// Proxy a fetch request through the CORS proxy
// Dev:     always proxy via http://localhost:3456?url={encoded}
// Hosted:  direct fetch first → fallback to /__cors-proxy?url={encoded}
export async function proxiedFetch(
  targetUrl: string,
  init?: RequestInit
): Promise<Response> {
  const headers = { ...((init?.headers as Record<string, string>) || {}) };

  // Dev mode: always proxy
  if (isDev) {
    const proxiedUrl = `${DEV_PROXY}?url=${encodeURIComponent(targetUrl)}`;
    return fetch(proxiedUrl, { ...init, headers });
  }

  // Hosted mode: try direct first (unless this origin is known to block CORS)
  const origin = getOrigin(targetUrl);

  if (!corsBlockedOrigins.has(origin)) {
    try {
      const directResponse = await fetch(targetUrl, { ...init, headers });
      // Success! This API supports direct CORS from our domain
      return directResponse;
    } catch (err) {
      // TypeError = network/CORS failure → remember and fall back to proxy
      if (err instanceof TypeError) {
        console.info(`[cors-proxy] Direct fetch to ${origin} blocked by CORS, falling back to proxy`);
        corsBlockedOrigins.add(origin);
      } else {
        throw err;
      }
    }
  }

  // Fallback: use the same-origin CORS proxy
  const proxiedUrl = `/__cors-proxy?url=${encodeURIComponent(targetUrl)}`;
  return fetch(proxiedUrl, { ...init, headers });
}

// Dedicated Jira fetch — Atlassian blocks CORS from browser origins,
// so always proxy: dev proxy on localhost, /__cors-proxy on hosted.
export async function jiraFetch(
  targetUrl: string,
  init?: RequestInit
): Promise<Response> {
  const headers = { ...((init?.headers as Record<string, string>) || {}) };

  if (isDev) {
    const proxiedUrl = `${DEV_PROXY}?url=${encodeURIComponent(targetUrl)}`;
    return fetch(proxiedUrl, { ...init, headers });
  }

  // Hosted: use same-origin CORS proxy (Atlassian blocks direct CORS)
  const proxiedUrl = `/__cors-proxy?url=${encodeURIComponent(targetUrl)}`;
  return fetch(proxiedUrl, { ...init, headers });
}

// Base64 encode for browser (replaces Node's Buffer.from)
export function base64Encode(str: string): string {
  return btoa(
    encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) =>
      String.fromCharCode(parseInt(p1, 16))
    )
  );
}

// Build Basic auth header value
export function buildBasicAuth(username: string, password: string): string {
  return `Basic ${base64Encode(`${username}:${password}`)}`;
}
